import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { InlineWidget, useCalendlyEventListener } from 'react-calendly';

import { ActionItemStatus } from '@ekhodealer/ekho-common';
import { Stack, Text, toast } from '@ekhodealer/ekho-common/rev';

import type { ActionItem, ServicesProviderSaleData } from '@ekhodealer/ekho-common';

import { useCurrentServicesProvider } from '@common/hooks';
import { CALENDLY_URL } from '@config/server/apiEndpoints';

import { useScheduleCounterSignRobosignSession } from '../../../api/progress/useScheduleCounterSignRobosignSession';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

interface ScheduleCounterSignRobosignSessionDetailProps {
	actionItem: ActionItem;
}

/**
 * ScheduleCounterSignRobosignSessionDetail component for scheduling a counter-signing
 * robotic session. Displays Calendly inline widget to allow dealers to schedule
 * their robotic signing session.
 */
export const ScheduleCounterSignRobosignSessionDetail = ({
	actionItem,
}: ScheduleCounterSignRobosignSessionDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const scheduleCounterSignMutation = useScheduleCounterSignRobosignSession(orderId);
	const { data: currentServicesProvider } = useCurrentServicesProvider();

	const [isScheduling, setIsScheduling] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Cleanup timeouts on unmount to prevent state updates after unmount
	useEffect(() => {
		return () => {
			if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
			if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
		};
	}, []);

	// Listen for Calendly event scheduled
	useCalendlyEventListener({
		onEventScheduled: async (e) => {
			setIsScheduling(true);
			setError(null);

			const { event, invitee } = e.data.payload;
			const eventId = event.uri.split('/').pop();
			const inviteeId = invitee.uri.split('/').pop();

			if (eventId && inviteeId) {
				try {
					await scheduleCounterSignMutation.mutateAsync({
						calendarData: {
							eventId,
							inviteeId,
						},
					});

					toast({
						message: 'Robotic signing session scheduled successfully',
						variant: 'success',
					});

					// Navigate back to progress view after short delay
					successTimeoutRef.current = setTimeout(() => {
						setIsScheduling(false);
						navigateBack();
					}, 1000);
				} catch (err) {
					console.error(err);
					setError('Something went wrong. Please try again or refresh the page.');
					setIsScheduling(false);

					// Clear error after 10 seconds
					if (errorTimeoutRef.current) {
						clearTimeout(errorTimeoutRef.current);
					}
					errorTimeoutRef.current = setTimeout(() => {
						setError(null);
					}, 10000);
				}
			} else {
				// Failed to parse event data from Calendly - reset loading state and show error
				setError('Failed to parse event data from Calendly. Please try again.');
				setIsScheduling(false);
			}
		},
	});

	// Get first agent from current services provider to prefill Calendly widget
	// Cast to ServicesProviderSaleData to access the agents array
	const saleProvider = currentServicesProvider as ServicesProviderSaleData | undefined;
	const firstAgent = saleProvider?.agents?.[0];
	const prefillName = firstAgent ? `${firstAgent.firstName} ${firstAgent.lastName}`.trim() : undefined;
	const prefillEmail = firstAgent?.email;

	const isComplete = actionItem.status === ActionItemStatus.COMPLETE;

	if (isComplete) {
		return (
			<Stack itemsSpacing='16'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					The counter-signing robotic session has already been scheduled.
				</Text>
			</Stack>
		);
	}

	return (
		<div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '16px' }}>
			<Stack itemsSpacing='8'>
				<Text >
					{
						'Schedule a remote signing session to counter-sign titling and registration documents for this order.'}
				</Text>
			</Stack>

			{error && (
				<Text size='bodySmall' color='error'>
					{error}
				</Text>
			)}

			{isScheduling ? (
				<Stack itemsSpacing='8'>
					<Text size='bodySmall' color='secondary'>
						Saving your scheduled session...
					</Text>
				</Stack>
			) : (
				<div style={{ width: '100%', flex: 1, minHeight: '700px' }}>
					<InlineWidget
						url={`${CALENDLY_URL}?hide_gdpr_banner=1`}
						styles={{ width: '100%', height: '100%', minHeight: '700px' }}
						prefill={{
							...(prefillName && { name: prefillName }),
							...(prefillEmail && { email: prefillEmail }),
						}}
					/>
				</div>
			)}
		</div>
	);
};

